import { useContext, useMemo } from 'react'

import { groupBy, sortBy } from 'lib/array/arrayUtils'
import useBedbankRates from 'hooks/useBedbankRates'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { isReservationAvailableForRoom } from 'lib/checkout/cartReservationUtils'
import { getPromotionMinLos, getBedbankCalendar, isFlashCalendarEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import getBedbankPromoExtraValue from 'lib/hotels/bedbankUtils'
import { useAppSelector } from './reduxHooks'
import { SALES_ENVIRONMENT_ENUM } from 'constants/bedbank'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { initialRooms } from 'lib/offer/occupancyUtils'

function getRatePrice(rate: App.BedbankRate) {
  return rate.totals.memberInclusive ? rate.totals.memberInclusive + (rate.totals.propertyFees ?? 0) : rate.totals.inclusive + (rate.totals.propertyFees ?? 0)
}

function useBedbankPackageViews(
  offer: App.BedbankOffer | undefined,
): [Array<App.BedbankOfferPackageView>, boolean] {
  const offerPageState = useContext(OfferPageStateContext)
  const {
    rooms = initialRooms,
    duration,
    checkInDate: checkIn,
    checkOutDate: checkOut,
  } = offerPageState
  const [{ hotelOnlyRates, hotelBundleRates }, fetchingRates] = useBedbankRates(offer?.id, rooms, checkIn, checkOut)

  const promotionMinLos = useMemo(() => getPromotionMinLos(offer), [offer])
  const calendarData = useAppSelector((state) => getBedbankCalendar(offerPageState, state))
  const isCalendarEnabled = useAppSelector((state) => isFlashCalendarEnabled(offerPageState, state))
  const promotions = useBedbankRoomRatePromotions(offer?.promotions, checkIn, checkOut)

  const result = useMemo(() => {
    if (!offer) {
      return []
    }

    const hasPromotions = !!offer?.promotions.length
    const hasValidPromotions = Object.values(promotions).length
    const validCheckinCheckoutDates = !!(checkIn && checkOut)

    const allPackages = [...offer.packages, ...hotelOnlyRates.map(rate => rate.room)]
    const roomTypes = Array.from(new Map(allPackages.map(pkg => ([pkg.roomId, pkg]))).values())
    const ratesGroup = groupBy(hotelOnlyRates, (rate) => rate.roomId)
    const packageViews = roomTypes.map(pkg => {
      const roomRates = hotelOnlyRates.filter(rate => rate.roomId === pkg.roomId)
      let sortedRates = sortBy(roomRates, rate => {
        const price = getRatePrice(rate)
        return price
      }, 'asc')

      let bundleRates = hotelBundleRates
      const promotionsRates = sortedRates.filter(rate => promotions[rate.id]?.id === pkg.promotionId).reduce<Record<string, App.BedbankPromotion>>((acc, rate) => {
        acc[rate.id] = promotions[rate.id]
        return acc
      }, {})

      const overrideValueAtPrice = hasPromotions && validCheckinCheckoutDates

      if (overrideValueAtPrice) {
        sortedRates = sortedRates.map((rate) => {
          if (promotions[rate.id]) {
            const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
              price: pkg.sell.price,
              value: pkg.sell.value,
              minLos: pkg.sell.los,
              duration,
            }) : 0
            const displayedHotelValue = inclusionsAmount + rate.totals.inclusive

            return {
              ...rate,
              value: Math.round(displayedHotelValue),
              discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
            }
          } else return rate
        })

        bundleRates = hotelBundleRates.map((bundleRate) => {
          if (promotions[bundleRate.id]) {
            const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
              price: pkg.sell.price,
              value: pkg.sell.value,
              minLos: pkg.sell.los,
              duration,
            }) : 0
            const displayedHotelValue = inclusionsAmount + bundleRate.totals.inclusive

            return {
              ...bundleRate,
              value: Math.round(displayedHotelValue),
              discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
            }
          } else return bundleRate
        })
      }

      const showAllRates = promotionMinLos > duration && isCalendarEnabled

      const showOnlyPromo = hasPromotions && !validCheckinCheckoutDates

      let show = false

      if (showOnlyPromo && showAllRates) {
        show = true
      } else if (hasPromotions) {
        const promo = offer.promotions.find((p) => p.id === pkg.promotionId)
        if (!validCheckinCheckoutDates && promo) {
          if ((isCalendarEnabled && calendarData?.[SALES_ENVIRONMENT_ENUM.hotelOnly]?.rooms?.some(x => x.id === pkg.roomId)) || !isCalendarEnabled) {
            show = true
          }
        } else if (validCheckinCheckoutDates && promo && hasValidPromotions) {
          show = true
        } else if (validCheckinCheckoutDates && !hasValidPromotions) {
          show = true
        }
      } else {
        show = true
      }

      // if there is a valid and applicable promotion, no checkin/checkout dates is defined,
      // we should add a "ghost" package rate to display the promotion (or at least I think that's what this code is doing)
      if (showOnlyPromo && !showAllRates) {
        const promo = offer.promotions.find((p) => p.id === pkg.promotionId)
        if (promo && pkg.sell && promo.los <= duration) {
          const price = Math.round((pkg.sell.price / pkg.sell.los) * duration * rooms.length)
          const value = Math.round((pkg.sell.value / pkg.sell.los) * duration * rooms.length)
          const taxesAndFees = Math.round((pkg.sell.taxesAndFees / pkg.sell.los) * duration * rooms.length)
          const propertyFees = Math.round((pkg.sell.propertyFees / pkg.sell.los) * duration * rooms.length)
          const discount = Number(((pkg.sell.value - pkg.sell.price) / pkg.sell.value).toFixed(2))
          sortedRates.push({
            id: promo.id,
            supplierCode: 'S0',
            boardCode: 'LE',
            bedGroups: [],
            roomId: pkg.roomId,
            room: pkg,
            groupId: undefined,
            occupancyPricing: [],
            totals: {
              exclusive: price,
              inclusive: price,
              taxesAndFees,
              propertyFees,
              total: price + propertyFees,
            },
            value,
            nights: duration,
            discount: discount > 0.02 ? discount : 0,
            memberDiscount: 0,
            refundable: false,
            partiallyRefundable: false,
            isFlightBundle: false,
            corporate: false,
            cancellationPolicies: [],
            cancellationPolicy: {
              policies: [],
              description: ['If you cancel your booking, you are not eligible for a refund or credit for a future stay.'],
            },
            inclusions: [],
          })
          promotionsRates[promo.id] = promo
        }
      }

      return {
        package: pkg,
        available: validCheckinCheckoutDates && ratesGroup.has(pkg.roomId) || !validCheckinCheckoutDates,
        capacityValid: rooms.every(room => isReservationAvailableForRoom(room, pkg.capacities.combinations, offer)),
        rates: sortedRates,
        bundleRates,
        cheapestRate: sortedRates[0],
        promotions: promotionsRates,
        show,
      }
    })
    return packageViews
  }, [offer, promotions, checkIn, checkOut, hotelOnlyRates, hotelBundleRates, promotionMinLos, duration, isCalendarEnabled, rooms, calendarData])

  return [result, fetchingRates]
}

export default useBedbankPackageViews
